import { motion } from "framer-motion";
import { GraduationCap, Stethoscope, Shield, BookOpen } from "lucide-react";

const credentials = [
  {
    icon: BookOpen,
    label: "Bachelor of Arts",
    value: "Communications · Fordham University",
  },
  {
    icon: GraduationCap,
    label: "Master of Science",
    value: "Speech-Language Pathology",
  },
  {
    icon: Stethoscope,
    label: "Clinical Certification",
    value: "ASHA CCC-SLP",
  },
  {
    icon: Shield,
    label: "Ireland Registration",
    value: "CORU Registered SLT",
  },
];

export const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-20 md:py-32"
    >
      <div
        className="orb orb-sage"
        style={{ width: 380, height: 380, top: 80, right: -140 }}
      />
      <div className="relative max-w-5xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="flex flex-wrap items-center gap-4 mb-10"
        >
          <img
            src="/logo.jpg"
            alt=""
            aria-hidden="true"
            data-testid="about-logo-badge"
            className="w-20 h-20 rounded-2xl ring-1 ring-black/5 shadow-sm"
          />
          <p className="text-xs uppercase tracking-[0.25em] text-[#5C6661]">
            About the Practitioner
          </p>
          <span
            data-testid="about-experience-badge"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#739072]/12 text-[#5C765B] text-xs uppercase tracking-[0.18em] font-medium"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#739072]" />
            10+ years of clinical experience
          </span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          data-testid="about-title"
          className="font-serif text-3xl md:text-5xl lg:text-6xl font-light tracking-tight text-[#2D332F] leading-[1.05] max-w-4xl"
        >
          Hi, I&rsquo;m{" "}
          <span className="text-[#2D332F]">Kathleen</span> —{" "}
          <em className="text-[#739072]">adult neurological care</em> is what
          I do.
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16"
        >
          <p className="md:col-span-6 text-[#2D332F]/85 text-base md:text-lg leading-relaxed">
            With over a decade of clinical experience, I founded Neurological
            Communication Partners to bring focused, evidence-based speech and
            language therapy to adults navigating Parkinson&rsquo;s disease,
            stroke recovery and cognitive change — on either side of the
            Atlantic.
          </p>
          <p className="md:col-span-6 text-[#2D332F]/85 text-base md:text-lg leading-relaxed">
            My approach blends rigorous clinical practice with the kind of
            listening that genuinely changes outcomes. We set goals together.
            We track progress honestly. And we celebrate the small wins that
            compound into real, lived independence.
          </p>
        </motion.div>

        <motion.figure
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mt-14 md:mt-16 border-l-2 border-[#739072] pl-6 md:pl-8 py-2 max-w-3xl"
        >
          <blockquote className="font-serif text-2xl md:text-3xl font-light leading-snug text-[#2D332F]">
            &ldquo;Therapy isn&rsquo;t about getting back to where you were —
            it&rsquo;s about getting to where you want to go from here.&rdquo;
          </blockquote>
          <figcaption className="mt-4 text-xs uppercase tracking-[0.22em] text-[#5C6661]">
            Kathleen McGrath, M.S. CCC-SLP
          </figcaption>
        </motion.figure>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-14 md:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {credentials.map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.label}
                data-testid={`credential-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                className="rounded-2xl border border-black/5 bg-[#FDFCF8] p-5"
              >
                <Icon
                  size={18}
                  strokeWidth={1.5}
                  className="text-[#739072] mb-3"
                />
                <p className="text-xs uppercase tracking-[0.18em] text-[#5C6661] mb-1.5">
                  {c.label}
                </p>
                <p className="font-medium text-[#2D332F] text-sm leading-snug">
                  {c.value}
                </p>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
